/**
 * logger — production-safe console wrapper with optional Sentry error capture.
 *
 * Console behaviour:
 *   In production builds (`NODE_ENV === 'production'`) every method except
 *   `error` is a no-op so diagnostic call sites stay in the codebase
 *   without leaking information or wasting cycles in shipped bundles.
 *   In development the methods proxy to the matching `console.*`.
 *
 * Optional Sentry capture (env-gated, dependency-free):
 *   If `REACT_APP_SENTRY_DSN` is set at build time, every `logger.error()`
 *   call also fires a single Sentry "envelope" POST. When the env var is
 *   unset (the default), there is ZERO behavioural change — no fetch, no
 *   imports, no work performed. Network failures, malformed DSNs, browser
 *   incompatibility, or Sentry being down all fail silently. There are
 *   intentionally no breadcrumbs, no replay, no performance tracing, no
 *   user context — only the raw error payload from the call site.
 *
 *   Reverting is one block deletion (the `Sentry capture` section below).
 *
 * Usage:
 *   import { logger } from '../lib/logger';
 *   logger.debug('voucher allocated', payload);
 *   logger.warn('contact channel missing', { method });
 *   logger.error('checkout failed', err);
 */

const isProd = (() => {
    try {
        return typeof process !== 'undefined'
            && process.env
            && process.env.NODE_ENV === 'production';
    } catch (_e) {
        return false;
    }
})();

const noop = () => {};

// ──────────────────────────────────────────────────────────────────────
// Sentry capture (dependency-free, env-gated, reversible)
//
// Parse the DSN once at module load. If unset OR malformed, `dsnParts`
// stays null and `sendToSentry` becomes a no-op — guaranteeing the
// "zero behaviour change when REACT_APP_SENTRY_DSN is unset" contract.
// ──────────────────────────────────────────────────────────────────────
const SENTRY_DSN = (() => {
    try {
        return (typeof process !== 'undefined'
            && process.env
            && process.env.REACT_APP_SENTRY_DSN) || null;
    } catch (_e) {
        return null;
    }
})();

const dsnParts = (() => {
    if (!SENTRY_DSN) return null;
    try {
        // Sentry DSN format: https://<PUBLIC_KEY>@<HOST>/<PROJECT_ID>
        const u = new URL(SENTRY_DSN);
        const projectId = u.pathname.replace(/^\/+/, '');
        if (!u.username || !u.host || !projectId) return null;
        return { host: u.host, projectId, publicKey: u.username };
    } catch (_e) {
        return null; // Malformed DSN — silent disable.
    }
})();

const randomEventId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID().replace(/-/g, '');
        }
    } catch (_e) { /* fall through */ }
    // Fallback: 32-char hex without dashes (RFC 4122 not required by Sentry)
    let s = '';
    for (let i = 0; i < 32; i += 1) s += Math.floor(Math.random() * 16).toString(16);
    return s;
};

const stringifyArg = (a) => {
    if (a instanceof Error) return a.stack || `${a.name}: ${a.message}`;
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); } catch (_e) { return String(a); }
};

const sendToSentry = (args) => {
    if (!dsnParts) return; // Env-gated short-circuit.
    try {
        const errArg = args.find((a) => a instanceof Error);
        const message = args.map(stringifyArg).filter(Boolean).join(' ');
        const eventId = randomEventId();
        const event = {
            event_id: eventId,
            timestamp: Date.now() / 1000,
            level: 'error',
            platform: 'javascript',
            logger: 'logger.error',
            message: { formatted: message.slice(0, 8192) || 'logger.error' },
            ...(errArg ? {
                exception: {
                    values: [{
                        type: errArg.name || 'Error',
                        value: (errArg.message || String(errArg)).slice(0, 2048),
                        // Raw stack as a single string frame — Sentry renders
                        // it acceptably without source-map symbolication and
                        // we explicitly opted out of breadcrumb / replay.
                        ...(errArg.stack ? { stacktrace: { frames: [{ filename: '<inline>', function: errArg.stack.slice(0, 4096) }] } } : {}),
                    }],
                },
            } : {}),
        };
        const envelope = [
            JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString(), dsn: SENTRY_DSN }),
            JSON.stringify({ type: 'event' }),
            JSON.stringify(event),
        ].join('\n');
        const url = `https://${dsnParts.host}/api/${dsnParts.projectId}/envelope/?sentry_key=${encodeURIComponent(dsnParts.publicKey)}&sentry_version=7`;
        // `keepalive: true` lets the request survive a page unload — useful
        // when the error happens during navigation away from a route.
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-sentry-envelope' },
            body: envelope,
            keepalive: true,
        }).catch(() => { /* silent: Sentry MUST NOT impact app behaviour */ });
    } catch (_e) {
        // Any unexpected exception (e.g. CSP-blocked fetch, environment
        // without crypto) is swallowed by design.
    }
};

// ──────────────────────────────────────────────────────────────────────


export const logger = {
    debug: isProd ? noop : (...args) => console.debug(...args),
    log:   isProd ? noop : (...args) => console.log(...args),
    info:  isProd ? noop : (...args) => console.info(...args),
    warn:  isProd ? noop : (...args) => console.warn(...args),
    // `error` is left ENABLED in production by design — surfacing
    // genuine error states is critical to debugging in the field.
    // If a Sentry DSN is configured, every error is also captured.
    // If you want to keep an error silent in production, branch on
    // `process.env.NODE_ENV` at the call site instead.
    error: (...args) => {
        sendToSentry(args);
        console.error(...args);
    },
};

export default logger;
